import { useState, useEffect } from 'react';
import './App.css';
import logoImg from './logo.png';

// ── Navbar ──────────────────────────────────────────────────────────────────
function Navbar({ onSignupClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-logo">
          <img src={logoImg} alt="InjuryPatient" className="nav-logo-img" />
          <span className="nav-logo-text">Injury<span>Patient</span></span>
        </div>
        <ul className="nav-links">
          <li><a href="#how-it-works" onClick={e => { e.preventDefault(); scrollTo('how-it-works'); }}>How It Works</a></li>
          <li><a href="#plans" onClick={e => { e.preventDefault(); scrollTo('plans'); }}>Plans</a></li>
          <li><a href="#features" onClick={e => { e.preventDefault(); scrollTo('features'); }}>Features</a></li>
          <li><a href="#signup" className="nav-cta" onClick={e => { e.preventDefault(); scrollTo('signup'); }}>Get Started</a></li>
        </ul>
        <button className="hamburger" onClick={() => setMenuOpen(true)} aria-label="Open menu">
          <span /><span /><span />
        </button>
      </nav>
      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        <button className="mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
        <a href="#how-it-works" onClick={() => scrollTo('how-it-works')}>How It Works</a>
        <a href="#plans" onClick={() => scrollTo('plans')}>Plans</a>
        <a href="#features" onClick={() => scrollTo('features')}>Features</a>
        <a href="#signup" onClick={() => scrollTo('signup')} style={{ color: 'var(--gold)' }}>Get Started →</a>
      </div>
    </>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="hero">
      <div className="hero-bg" />
      <div className="hero-grid" />
      <div className="hero-glow" />
      <div className="hero-content">
        <div>
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Privacy-Protected Legal Platform
          </div>
          <h1 className="hero-title">
            Connect with Top<br />
            <span className="highlight">Injury Attorneys</span><br />
            Anonymously
          </h1>
          <p className="hero-subtitle">
            InjuryPatient.com bridges the gap between injured patients and qualified
            attorneys — with complete privacy protection until you choose to engage.
          </p>
          <div className="hero-actions">
            <button className="btn-primary" onClick={() => scrollTo('signup')}>
              🤕 I'm a Patient — Get Help
            </button>
            <button className="btn-secondary" onClick={() => scrollTo('signup')}>
              ⚖️ I'm an Attorney
            </button>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-logo-wrap">
            <img src={logoImg} alt="InjuryPatient.com" className="hero-logo-img" />
            <p className="hero-logo-name">Injury<span>Patient</span>.com</p>
            <p className="hero-logo-sub">Privacy-Protected Legal Platform</p>
            <div className="hero-logo-tags">
              {['🔒 Anonymous', '⚖️ Verified Attorneys', '⚡ 24hr Match'].map((t) => (
                <span className="hero-logo-tag" key={t}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Banner ──────────────────────────────────────────────────────────────────
function Banner() {
  return (
    <section className="banner-section">
      <img src="/injury-banner.svg" alt="InjuryPatient banner" className="banner-img" />
    </section>
  );
}

// ── Trust Bar ────────────────────────────────────────────────────────────────
function TrustBar() {
  const items = [
    { icon: '🔒', text: 'End-to-End Encrypted' },
    { icon: '⚖️', text: 'Verified Attorneys Only' },
    { icon: '🛡️', text: 'HIPAA Compliant' },
    { icon: '⚡', text: 'Match in 24 Hours' },
    { icon: '🤝', text: 'No Hidden Fees for Patients' },
  ];
  return (
    <div className="trust-bar">
      <div className="trust-items">
        {items.map((item, i) => (
          <div className="trust-item" key={i}>
            <span className="trust-icon">{item.icon}</span>
            <span>{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── How It Works ─────────────────────────────────────────────────────────────
const HOW_DATA = {
  patient: [
    { n: '01', title: 'Register & Submit Your Case', desc: 'Create an anonymous profile and describe your injury. Your identity remains completely hidden from attorneys.' },
    { n: '02', title: 'Choose Free or Elite Plan', desc: 'Free plan gives you basic matching. Elite unlocks medical record uploads, priority placement, and case scoring.' },
    { n: '03', title: 'Attorneys Reach Out Blindly', desc: 'Qualified attorneys review your anonymized case and send interest requests through our blind communication system.' },
    { n: '04', title: 'Review & Engage Securely', desc: 'Compare attorney profiles and reviews. Once you choose, securely sign the engagement agreement through the platform.' },
  ],
  attorney: [
    { n: '01', title: 'Subscribe to Access Cases', desc: 'Choose a subscription tier to access our database of scored, anonymized injury cases matching your practice areas.' },
    { n: '02', title: 'Browse Scored Case Listings', desc: 'Our AI-powered case scoring system rates each case by strength and potential value — saving your review time.' },
    { n: '03', title: 'Pay Acquisition Fee & Connect', desc: 'When you find a compelling case, pay a transparent acquisition fee to initiate blind communication with the patient.' },
    { n: '04', title: 'Engage with E-Signature', desc: 'Complete the engagement workflow digitally. Signed retainer agreements, all in one secure platform.' },
  ],
};

function HowItWorks() {
  const [tab, setTab] = useState('patient');
  const steps = HOW_DATA[tab];

  return (
    <section className="section how-section" id="how-it-works">
      <div className="section-center">
        <p className="section-eyebrow">How It Works</p>
        <h2 className="section-title">Simple. Secure. Effective.</h2>
        <p className="section-sub">A streamlined process designed to protect patients while delivering qualified leads to attorneys.</p>
      </div>
      <div className="how-tabs">
        <button className={`how-tab ${tab === 'patient' ? 'active' : ''}`} onClick={() => setTab('patient')}>🤕 For Patients</button>
        <button className={`how-tab ${tab === 'attorney' ? 'active' : ''}`} onClick={() => setTab('attorney')}>⚖️ For Attorneys</button>
      </div>
      <div className="how-steps">
        {steps.map((s) => (
          <div className="how-step" key={s.n}>
            <div className="step-number">{s.n}</div>
            <div>
              <p className="step-content-title">{s.title}</p>
              <p className="step-content-desc">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Plans ────────────────────────────────────────────────────────────────────
const PLANS = [
  {
    emoji: '🆓',
    name: 'Patient — Free',
    tagline: 'Submit your case and get matched at no cost.',
    perks: ['Anonymous case submission', 'Attorney matching', 'Secure blind messaging'],
    btn: 'Sign Up Free',
    btnClass: 'plan-btn-outline',
  },
  {
    emoji: '⭐',
    name: 'Patient — Elite',
    tagline: 'Priority placement and medical record upload.',
    perks: ['Everything in Free', 'Medical record upload', 'Priority case listing', 'Case score & level badge'],
    btn: 'Go Elite',
    btnClass: 'plan-btn-white',
    featured: true,
    badge: 'Most Popular',
  },
  {
    emoji: '⚖️',
    name: 'Attorney Access',
    tagline: 'Subscribe to browse and acquire scored cases.',
    perks: ['Browse anonymized cases', 'Case scoring & filters', 'Acquisition fee workflow', 'E-signature engagement'],
    btn: 'Apply as Attorney',
    btnClass: 'plan-btn-red',
    attorney: true,
  },
];

function Plans() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <section className="section plans-section" id="plans">
      <div className="section-center">
        <p className="section-eyebrow">Plans</p>
        <h2 className="section-title">Simple, Transparent Pricing</h2>
        <p className="section-sub">Patients stay anonymous on every plan. Attorneys get verified before access.</p>
      </div>
      <div className="plans-simple-grid">
        {PLANS.map((p) => (
          <div className={`plan-simple-card ${p.featured ? 'featured' : ''} ${p.attorney ? 'attorney-card' : ''}`} key={p.name}>
            {p.badge && <div className="plan-badge">{p.badge}</div>}
            <span className="plan-emoji">{p.emoji}</span>
            <h3 className="plan-name">{p.name}</h3>
            <button className={`plan-btn ${p.btnClass}`} onClick={() => scrollTo('signup')}>{p.btn}</button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Features ─────────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: '🔒', title: 'Anonymous Case Submission', desc: 'Submit your injury case without revealing your identity. Attorneys see your case — not who you are.' },
  { icon: '💬', title: 'Blind Messaging', desc: 'Communicate with multiple attorneys through our secure messaging system while staying completely anonymous.' },
  { icon: '🏥', title: 'Medical Record Upload', desc: 'Elite patients can upload medical records directly to their case profile for attorneys to review.' },
  { icon: '📋', title: 'E-Signature Engagement', desc: 'Sign retainer agreements digitally through the platform once you choose your attorney.' },
  { icon: '💰', title: 'Case Acquisition Fee', desc: 'Attorneys pay a transparent fee to access patient contact details — keeping the platform free for patients.' },
  { icon: '🛡️', title: 'Privacy Until Engagement', desc: 'Your personal details are locked until you explicitly consent to engage with a specific attorney.' },
];

function Features() {
  return (
    <section className="section features-section" id="features">
      <div className="section-center">
        <p className="section-eyebrow">Platform Features</p>
        <h2 className="section-title">Built for Protection & Results</h2>
        <p className="section-sub">Every feature is designed with your privacy and case success in mind.</p>
      </div>
      <div className="features-grid">
        {FEATURES.map((f) => (
          <div className="feature-card" key={f.title}>
            <div className="feature-icon-wrap">{f.icon}</div>
            <h3 className="feature-title">{f.title}</h3>
            <p className="feature-desc">{f.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Testimonials ──────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  { stars: '★★★★★', text: '"I was terrified to reach out to attorneys after my accident. InjuryPatient let me stay anonymous while multiple attorneys competed for my case. I ended up with a great settlement."', name: 'Sarah M.', role: 'Auto Accident Survivor', initial: 'S' },
  { stars: '★★★★★', text: '"The platform brings us pre-screened, scored cases. The acquisition fee model is transparent and fair. We\'ve signed 40+ cases through InjuryPatient in our first year."', name: 'James R., Esq.', role: 'Personal Injury Attorney', initial: 'J' },
  { stars: '★★★★★', text: '"The Elite plan was worth every penny. Having my medical records attached to my profile meant attorneys could actually evaluate my case properly before reaching out."', name: 'David K.', role: 'Slip & Fall Patient', initial: 'D' },
];

function Testimonials() {
  return (
    <section className="testimonials-section" id="testimonials">
      <div className="section-center">
        <p className="section-eyebrow">Real Stories</p>
        <h2 className="section-title">Trusted by Patients & Attorneys</h2>
      </div>
      <div className="testimonials-grid">
        {TESTIMONIALS.map((t) => (
          <div className="testimonial-card" key={t.name}>
            <div className="stars">{t.stars}</div>
            <p className="testimonial-text">{t.text}</p>
            <div className="testimonial-author">
              <div className="author-avatar">{t.initial}</div>
              <div>
                <div className="author-name">{t.name}</div>
                <div className="author-role">{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Signup Section ────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'https://api.injurypatient.com';

function PatientForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', caseType: '', plan: 'free', city: '', state: '', serviceArea: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/patients/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSubmitted(true);
      setTimeout(() => window.location.reload(), 3000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) return (
    <div className="success-message">
      <div className="success-icon">✓</div>
      <h3 className="success-title">You're on the List!</h3>
      <p className="success-sub">We'll notify you at <strong>{form.email}</strong> when InjuryPatient launches. Your anonymity is our priority.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      {error && <p className="form-error">{error}</p>}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">First Name</label>
          <input className="form-input" placeholder="Jane" value={form.firstName} onChange={set('firstName')} required />
        </div>
        <div className="form-group">
          <label className="form-label">Last Name</label>
          <input className="form-input" placeholder="Doe" value={form.lastName} onChange={set('lastName')} required />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Email Address</label>
        <input className="form-input" type="email" placeholder="jane@email.com" value={form.email} onChange={set('email')} required />
      </div>
      <div className="form-group">
        <label className="form-label">Phone Number</label>
        <input className="form-input" type="tel" placeholder="+1 (555) 000-0000" value={form.phone} onChange={set('phone')} />
      </div>
      <div className="form-group">
        <label className="form-label">Case Type</label>
        <select className="form-select" value={form.caseType} onChange={set('caseType')} required>
          <option value="">Select type...</option>
          <option>Auto Accident</option>
          <option>Medical Malpractice</option>
          <option>Slip & Fall</option>
          <option>Workers' Comp</option>
          <option>Product Liability</option>
          <option>Other</option>
        </select>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">City</label>
          <input className="form-input" placeholder="Los Angeles" value={form.city} onChange={set('city')} />
        </div>
        <div className="form-group">
          <label className="form-label">State</label>
          <input className="form-input" placeholder="California" value={form.state} onChange={set('state')} required />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Service Area</label>
        <input className="form-input" placeholder="e.g. Greater Los Angeles, Southern California" value={form.serviceArea} onChange={set('serviceArea')} />
      </div>
      <div className="form-group">
        <label className="form-label">Plan</label>
        <select className="form-select" value={form.plan} onChange={set('plan')}>
          <option value="free">Free Plan — Basic Matching</option>
          <option value="elite">Elite Plan — Priority + Medical Records</option>
        </select>
      </div>
      <button type="submit" className="submit-btn" disabled={loading}>
        {loading ? 'Submitting…' : '🤕 Register as Patient →'}
      </button>
      <p className="form-privacy">By registering you agree to our <a href="#privacy">Privacy Policy</a>. Your identity stays hidden until you engage.</p>
    </form>
  );
}

function AttorneyForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', firm: '', barNumber: '', practiceArea: '', city: '', state: '', serviceArea: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/attorneys/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSubmitted(true);
      setTimeout(() => window.location.reload(), 3000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) return (
    <div className="success-message">
      <div className="success-icon" style={{ background: 'rgba(30,58,110,0.2)', borderColor: '#4a7fd4' }}>✓</div>
      <h3 className="success-title">Application Received!</h3>
      <p className="success-sub">We'll review your credentials and contact you at <strong>{form.email}</strong> within 48 hours to activate your attorney account.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      {error && <p className="form-error">{error}</p>}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">First Name</label>
          <input className="form-input" placeholder="John" value={form.firstName} onChange={set('firstName')} required />
        </div>
        <div className="form-group">
          <label className="form-label">Last Name</label>
          <input className="form-input" placeholder="Smith" value={form.lastName} onChange={set('lastName')} required />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Work Email</label>
        <input className="form-input" type="email" placeholder="john@smithlaw.com" value={form.email} onChange={set('email')} required />
      </div>
      <div className="form-group">
        <label className="form-label">Law Firm Name</label>
        <input className="form-input" placeholder="Smith & Associates LLP" value={form.firm} onChange={set('firm')} required />
      </div>
      <div className="form-group">
        <label className="form-label">Bar Number</label>
        <input className="form-input" placeholder="BA-12345" value={form.barNumber} onChange={set('barNumber')} required />
      </div>
      <div className="form-group">
        <label className="form-label">Primary Practice Area</label>
        <select className="form-select" value={form.practiceArea} onChange={set('practiceArea')} required>
          <option value="">Select area...</option>
          <option>Personal Injury</option>
          <option>Medical Malpractice</option>
          <option>Workers' Compensation</option>
          <option>Auto Accident</option>
          <option>Product Liability</option>
          <option>General Practice</option>
        </select>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">City</label>
          <input className="form-input" placeholder="Los Angeles" value={form.city} onChange={set('city')} />
        </div>
        <div className="form-group">
          <label className="form-label">State</label>
          <input className="form-input" placeholder="California" value={form.state} onChange={set('state')} required />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Service Area</label>
        <input className="form-input" placeholder="e.g. Greater Los Angeles, Southern California" value={form.serviceArea} onChange={set('serviceArea')} />
      </div>
      <button type="submit" className="submit-btn submit-btn-attorney" disabled={loading}>
        {loading ? 'Submitting…' : '⚖️ Apply as Attorney →'}
      </button>
      <p className="form-privacy">Attorney accounts are manually verified within 48 hours. Bar credentials required. <a href="#terms">Terms apply</a>.</p>
    </form>
  );
}

function SignupSection() {
  return (
    <section className="signup-section" id="signup">
      <div className="signup-wrapper">
        <div className="signup-header">
          <p className="section-eyebrow">Join InjuryPatient</p>
          <h2 className="section-title">Get Early Access Today</h2>
          <p className="section-sub" style={{ margin: '0 auto', color: 'rgba(255,255,255,0.6)' }}>
            Be among the first patients and attorneys on the platform. Sign up now to secure your spot.
          </p>
        </div>
        <div className="signup-grid">
          <div className="signup-card">
            <div className="signup-card-header">
              <div className="signup-card-icon">🤕</div>
              <div>
                <h3 className="signup-card-title">I'm a Patient</h3>
                <p className="signup-card-sub">Find the right attorney anonymously</p>
              </div>
            </div>
            <PatientForm />
          </div>
          <div className="signup-card">
            <div className="signup-card-header">
              <div className="signup-card-icon attorney">⚖️</div>
              <div>
                <h3 className="signup-card-title">I'm an Attorney</h3>
                <p className="signup-card-sub">Access qualified injury cases</p>
              </div>
            </div>
            <AttorneyForm />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── App Store Section ─────────────────────────────────────────────────────────
function AppSection() {
  return (
    <section className="app-section">
      <p className="section-eyebrow">Mobile App</p>
      <h2 className="section-title" style={{ marginBottom: 8 }}>Take It With You</h2>
      <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.55)', maxWidth: 440, margin: '0 auto' }}>
        The InjuryPatient mobile app is coming soon to iOS and Android. Register now to get notified at launch.
      </p>
      <div className="app-badges">
        <div className="app-badge-wrap">
          <img src="/appstore-badge.svg" alt="Download on the App Store" className="store-badge-img" />
          <span className="store-badge-soon">Coming Soon</span>
        </div>
        <div className="app-badge-wrap">
          <img src="/playstore-badge.svg" alt="Get it on Google Play" className="store-badge-img" />
          <span className="store-badge-soon">Coming Soon</span>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <footer className="footer-simple">
      <div className="footer-simple-inner">
        <div className="nav-logo">
          <img src={logoImg} alt="InjuryPatient" className="footer-logo-img" />
          <span className="nav-logo-text">Injury<span>Patient</span>.com</span>
        </div>
        <div className="footer-simple-links">
          <a href="#how-it-works" onClick={e => { e.preventDefault(); scrollTo('how-it-works'); }}>How It Works</a>
          <a href="#plans" onClick={e => { e.preventDefault(); scrollTo('plans'); }}>Plans</a>
          <a href="#features" onClick={e => { e.preventDefault(); scrollTo('features'); }}>Features</a>
          <a href="#signup" onClick={e => { e.preventDefault(); scrollTo('signup'); }}>Sign Up</a>
        </div>
        <span className="footer-simple-copy">© {new Date().getFullYear()} InjuryPatient.com</span>
      </div>
    </footer>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <div>
      <Navbar />
      <Hero />
      <Banner />
      <HowItWorks />
      <Plans />
      <Features />
      <SignupSection />
      <AppSection />
      <Footer />
    </div>
  );
}
